import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { api } from "@/lib/api"
import { decryptField, encryptField, searchHash } from "@/lib/crypto"
import { useCryptoStore } from "@/store/crypto"

export interface Person {
  id: string
  householdId: string
  name: string
  upiHandles: string[]
  phoneNumbers: string[]
  relationship: string | null
  notes: string | null
  /** Non-null when the record is client-encrypted (private mode); decrypt to get name. */
  cipher?: string | null
  createdAt: string
  updatedAt: string
}

interface ListResponse {
  success: boolean
  data: Person[]
}

interface SingleResponse {
  success: boolean
  data: Person
}

export interface PersonInput {
  name: string
  upiHandles?: string[]
  phoneNumbers?: string[]
  relationship?: string | null
  notes?: string | null
}

export function usePersons() {
  return useQuery({
    queryKey: ["persons"],
    queryFn: () => api.get<ListResponse>("/persons"),
  })
}

/**
 * Persons with names decrypted client-side. In private mode the API returns
 * `name: ""` + a `cipher` blob (it can't read the household key), so any UI that
 * shows person names — the settings tab, the transaction person filter — must
 * decrypt here. Falls back to the plaintext name in standard mode.
 */
export function useDecryptedPersons() {
  const query = usePersons()
  const key = useCryptoStore((s) => s.key)
  const [persons, setPersons] = useState<Person[] | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    const rows = query.data?.data
    if (!rows || !key) {
      setPersons(rows)
      return
    }
    Promise.all(
      rows.map(async (p) => {
        if (!p.cipher) return p
        try {
          const fields = JSON.parse(await decryptField(p.cipher, key)) as Partial<Person> & {
            name: string
          }
          return {
            ...p,
            name: fields.name,
            upiHandles: fields.upiHandles ?? p.upiHandles,
            phoneNumbers: fields.phoneNumbers ?? p.phoneNumbers,
            relationship: fields.relationship ?? p.relationship,
            notes: fields.notes ?? p.notes,
          }
        } catch {
          return p
        }
      }),
    ).then((decoded) => {
      if (!cancelled) setPersons(decoded)
    })
    return () => {
      cancelled = true
    }
  }, [query.data, key])

  return { ...query, data: persons }
}

/**
 * Request body for a person write. Private mode encrypts every field on the
 * device and sends only { cipher, searchHash } (same format as MCP), so the
 * server never sees the name. Fails closed if the keys aren't ready.
 */
export async function personBody(input: PersonInput) {
  const { mode, key, hmacKey } = useCryptoStore.getState()
  if (mode !== "private") return input
  if (!key || !hmacKey) throw new Error("Unlock private mode to save people")
  const fields = {
    name: input.name,
    upiHandles: input.upiHandles ?? [],
    phoneNumbers: input.phoneNumbers ?? [],
    relationship: input.relationship ?? null,
    notes: input.notes ?? null,
  }
  return {
    cipher: await encryptField(JSON.stringify(fields), key),
    searchHash: await searchHash(fields.name.toLowerCase().trim().replace(/\s+/g, " "), hmacKey),
  }
}

export function useCreatePerson() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: PersonInput) =>
      api.post<SingleResponse>("/persons", await personBody(body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["persons"] }),
  })
}

export function useUpdatePerson() {
  const qc = useQueryClient()
  return useMutation({
    // Full fields: a private cipher replaces the whole record.
    mutationFn: async ({ id, ...body }: { id: string } & PersonInput) =>
      api.patch<SingleResponse>(`/persons/${id}`, await personBody(body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["persons"] }),
  })
}

export function useDeletePerson() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ success: boolean }>(`/persons/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["persons"] }),
  })
}
